/**
 * CMP要素管理（template[data-cmp-category]のDOM展開・MutationObserver）
 *
 * 同意済みカテゴリの<template>の中身をDOMに展開（アクティベート）する。
 * MutationObserverで動的に追加された<template>も監視・制御する。
 */

let observer = null;
let consentChecker = null; // hasConsent関数の参照

/**
 * template要素をアクティベートする
 * <template>から取り出した<script>はそのままDOMに挿入しても実行されないため、
 * document.createElement('script')で新規作成し属性と内容をコピーする。
 * @param {HTMLTemplateElement} template
 */
function activateTemplate(template) {
  const fragment = template.content.cloneNode(true);

  // script要素は再作成が必要
  fragment.querySelectorAll('script').forEach((original) => {
    const script = document.createElement('script');
    original.getAttributeNames().forEach((name) => {
      script.setAttribute(name, original.getAttribute(name));
    });
    if (original.textContent) {
      script.textContent = original.textContent;
    }
    original.replaceWith(script);
  });

  template.replaceWith(fragment);
}

/**
 * ページ内の全template[data-cmp-category]を処理する
 * @param {Function} hasConsent - カテゴリの同意状態を返す関数
 */
export function processTemplates(hasConsent) {
  const templates = document.querySelectorAll('template[data-cmp-category]');
  templates.forEach((template) => {
    const category = template.getAttribute('data-cmp-category');
    if (hasConsent(category)) {
      activateTemplate(template);
    }
  });
}

/**
 * MutationObserverを開始する
 * 動的に追加されたtemplate[data-cmp-category]を監視し、同意済みなら即アクティベートする。
 * @param {Function} hasConsent - カテゴリの同意状態を返す関数
 */
export function startObserver(hasConsent) {
  consentChecker = hasConsent;

  if (observer) {
    observer.disconnect();
  }

  let pending = false;
  observer = new MutationObserver((mutations) => {
    if (pending) return;
    pending = true;
    queueMicrotask(() => {
      pending = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;

          // 追加されたノード自体がtemplate[data-cmp-category]の場合
          if (node.matches && node.matches('template[data-cmp-category]')) {
            const category = node.getAttribute('data-cmp-category');
            if (consentChecker(category)) {
              activateTemplate(node);
            }
          }

          // 追加されたノードの子孫にtemplate[data-cmp-category]がある場合
          if (node.querySelectorAll) {
            node.querySelectorAll('template[data-cmp-category]').forEach((tmpl) => {
              const category = tmpl.getAttribute('data-cmp-category');
              if (consentChecker(category)) {
                activateTemplate(tmpl);
              }
            });
          }
        }
      }
    });
  });

  observer.observe(document.body, { subtree: true, childList: true });
}

/**
 * MutationObserverを停止する
 */
export function stopObserver() {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  consentChecker = null;
}
